/**
 * Pure geometry for flowchart edge rendering.
 *
 * Edges are routed ORTHOGONALLY (right-angle segments with rounded corners) via
 * react-flow's `getSmoothStepPath`. Each end is anchored not to a node's
 * bounding box but to the point where a ray leaving the node in its cardinal
 * flow direction (down for a top-down source, up for its target, etc.) meets the
 * node's actual SILHOUETTE — its inscribed polygon for a diamond / parallelogram
 * / trapezoid / hexagon, its inscribed circle for the connector, the bbox edge
 * for every rounded-rect shape. So the path leaves a diamond exactly at its
 * bottom vertex and a rectangle at its bottom-edge midpoint, and the stepped
 * route reads as a clean circuit-board trace rather than a diagonal.
 *
 * The silhouette for each shape is derived from the exact clip-path / radius
 * used to render the node (see `flowchart-shapes.tsx`), so the endpoints match
 * what the eye sees.
 *
 * No React or DOM assumptions live here — only numbers, the {@link Position}
 * enum, and the pure {@link FlowchartNodeShape} enum in, numbers/strings out —
 * so the math stays unit-reasonable and testable.
 */

import { Position, getSmoothStepPath } from '@xyflow/react';
import { FlowchartNodeShape } from './parse-mermaid-flowchart';

export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface NodeRect {
  /** Center of the node in flow coordinates. */
  center: Point;
  size: Size;
  /** Shape used to render the node, so the border clip matches its silhouette. */
  shape: FlowchartNodeShape;
}

export interface EdgeEndpoints {
  source: Point;
  target: Point;
}

/** Clip a node's silhouette (not just its bbox) to find an edge endpoint. */
export interface ShapeBorderPointInput {
  rect: NodeRect;
  /** A point the edge is heading toward (used to aim the ray from the center). */
  towardPoint: Point;
}

/**
 * A silhouette vertex expressed as a fraction of the node rect: `(0,0)` is the
 * top-left corner, `(1,1)` the bottom-right. These mirror the `polygon(... %)`
 * clip-paths in `flowchart-shapes.tsx` exactly, so the edge meets the shape
 * where it is actually drawn.
 */
interface UnitVertex {
  fx: number;
  fy: number;
}

interface RayVector {
  x: number;
  y: number;
}

interface PolygonEdge {
  start: Point;
  end: Point;
}

interface RayEdgeParameterInput {
  edge: PolygonEdge;
  origin: Point;
  ray: RayVector;
}

/**
 * Convex silhouette outlines in unit space, keyed by shape. Only the
 * straight-edged polygons whose drawn silhouette is inset from the bounding box
 * appear here; rounded-rect shapes (process / terminator / database / document /
 * predefined) fall back to the bbox clip, and the connector circle is handled
 * analytically. Vertices are listed clockwise.
 *
 *  - Decision  → inscribed diamond  polygon(50% 0, 100% 50, 50% 100, 0 50)
 *  - InputOutput (parallelogram)    polygon(22% 0, 100% 0, 78% 100, 0 100)
 *  - Trapezoid                      polygon(18% 0, 82% 0, 100% 100, 0 100)
 *  - Hexagon                        polygon(25% 0, 75% 0, 100% 50, 75% 100, 25% 100, 0 50)
 */
const SILHOUETTE_VERTICES: Partial<Record<FlowchartNodeShape, readonly UnitVertex[]>> = {
  [FlowchartNodeShape.Decision]: [
    { fx: 0.5, fy: 0 },
    { fx: 1, fy: 0.5 },
    { fx: 0.5, fy: 1 },
    { fx: 0, fy: 0.5 },
  ],
  [FlowchartNodeShape.InputOutput]: [
    { fx: 0.22, fy: 0 },
    { fx: 1, fy: 0 },
    { fx: 0.78, fy: 1 },
    { fx: 0, fy: 1 },
  ],
  [FlowchartNodeShape.Trapezoid]: [
    { fx: 0.18, fy: 0 },
    { fx: 0.82, fy: 0 },
    { fx: 1, fy: 1 },
    { fx: 0, fy: 1 },
  ],
  [FlowchartNodeShape.Hexagon]: [
    { fx: 0.25, fy: 0 },
    { fx: 0.75, fy: 0 },
    { fx: 1, fy: 0.5 },
    { fx: 0.75, fy: 1 },
    { fx: 0.25, fy: 1 },
    { fx: 0, fy: 0.5 },
  ],
};

export interface SelfLoopInput {
  center: Point;
  size: Size;
  radius: number;
}

export interface SelfLoopGeometry {
  path: string;
  /** Where the edge label should sit for a self-loop. */
  labelPosition: Point;
}

export interface ParallelPathInput {
  source: Point;
  target: Point;
  /** Signed perpendicular offset; 0 means a straight line. */
  offset: number;
}

export interface ParallelGeometry {
  path: string;
  labelPosition: Point;
}

/** Half a pixel of slack so the stroke meets the border cleanly without a seam. */
const BORDER_INSET = 0.5;
/** Below this center-to-center distance the nodes overlap; clamp to avoid NaN. */
const MIN_CENTER_DISTANCE = 0.001;
/** Slack on a polygon edge parameter so a ray that exits exactly at a vertex still counts. */
const EDGE_PARAM_EPSILON = 1e-6;
/**
 * How far past the node a cardinal probe point is placed. Any distance well
 * beyond the node's own half-extent yields the same border hit; a fixed large
 * value keeps the ray purely axis-aligned regardless of node size.
 */
const CARDINAL_PROBE_REACH = 10000;

function clamp(value: number, min: number, max: number): number {
  if (value < min) {
    return min;
  }
  if (value > max) {
    return max;
  }
  return value;
}

/** Unit vector pointing out of a node in the direction of a cardinal {@link Position}. */
function positionUnitVector(position: Position): Point {
  if (position === Position.Top) {
    return { x: 0, y: -1 };
  }
  if (position === Position.Bottom) {
    return { x: 0, y: 1 };
  }
  if (position === Position.Left) {
    return { x: -1, y: 0 };
  }
  return { x: 1, y: 0 };
}

/**
 * The point on an axis-aligned rectangle's border that lies on the ray from the
 * rectangle's center toward `towards`.
 *
 * We scale the direction vector so it just reaches the nearest of the four
 * edges. For a rectangle of half-width `hw` and half-height `hh`, the border is
 * hit at `t = min(hw / |dx|, hh / |dy|)` along the unit-free direction
 * `(dx, dy)`. A small inset pulls the point a hair inside so round caps and
 * markers seat flush against the visible border.
 */
function getNodeBorderPoint(input: { center: Point; size: Size; towards: Point }): Point {
  const { center, size, towards } = input;
  const dx = towards.x - center.x;
  const dy = towards.y - center.y;
  const halfWidth = size.width / 2;
  const halfHeight = size.height / 2;

  if (Math.abs(dx) < MIN_CENTER_DISTANCE && Math.abs(dy) < MIN_CENTER_DISTANCE) {
    // Concentric probe: nothing sensible to clip to; sit on the top border.
    return { x: center.x, y: center.y - Math.max(halfHeight - BORDER_INSET, 0) };
  }

  const scaleX = Math.abs(dx) < MIN_CENTER_DISTANCE ? Number.POSITIVE_INFINITY : halfWidth / Math.abs(dx);
  const scaleY = Math.abs(dy) < MIN_CENTER_DISTANCE ? Number.POSITIVE_INFINITY : halfHeight / Math.abs(dy);
  const scale = Math.min(scaleX, scaleY);
  const insetScale = clamp((scale * Math.hypot(dx, dy) - BORDER_INSET) / Math.hypot(dx, dy), 0, scale);

  return {
    x: center.x + dx * insetScale,
    y: center.y + dy * insetScale,
  };
}

/** A node's silhouette vertices in absolute flow coordinates (clockwise). */
function silhouettePolygon(rect: NodeRect): readonly Point[] | null {
  const vertices = SILHOUETTE_VERTICES[rect.shape];
  if (!vertices) {
    return null;
  }
  const left = rect.center.x - rect.size.width / 2;
  const top = rect.center.y - rect.size.height / 2;
  return vertices.map((vertex) => ({
    x: left + vertex.fx * rect.size.width,
    y: top + vertex.fy * rect.size.height,
  }));
}

/** Pull a silhouette point a hair toward the center so caps/markers seat flush. */
function insetTowardCenter(input: { point: Point; center: Point }): Point {
  const dx = input.point.x - input.center.x;
  const dy = input.point.y - input.center.y;
  const length = Math.hypot(dx, dy);
  if (length < MIN_CENTER_DISTANCE) {
    return input.point;
  }
  const reach = Math.max(length - BORDER_INSET, 0);
  return {
    x: input.center.x + (dx / length) * reach,
    y: input.center.y + (dy / length) * reach,
  };
}

function resolveRayVector(input: { origin: Point; towards: Point }): RayVector | null {
  const ray: RayVector = {
    x: input.towards.x - input.origin.x,
    y: input.towards.y - input.origin.y,
  };

  if (Math.abs(ray.x) < MIN_CENTER_DISTANCE && Math.abs(ray.y) < MIN_CENTER_DISTANCE) {
    return null;
  }

  return ray;
}

function polygonEdgeAt(input: {
  edgeIndex: number;
  polygon: readonly Point[];
}): PolygonEdge | null {
  const start = input.polygon[input.edgeIndex];
  const end = input.polygon[(input.edgeIndex + 1) % input.polygon.length];

  if (!start || !end) {
    return null;
  }

  return { start, end };
}

function getRayEdgeParameter(input: RayEdgeParameterInput): number | null {
  const edgeX = input.edge.end.x - input.edge.start.x;
  const edgeY = input.edge.end.y - input.edge.start.y;
  const denom = input.ray.x * edgeY - input.ray.y * edgeX;

  if (Math.abs(denom) < MIN_CENTER_DISTANCE) {
    return null;
  }

  const relX = input.edge.start.x - input.origin.x;
  const relY = input.edge.start.y - input.origin.y;
  const rayParameter = (relX * edgeY - relY * edgeX) / denom;
  const edgeParameter = (relX * input.ray.y - relY * input.ray.x) / denom;

  if (!isForwardEdgeHit({ edgeParameter, rayParameter })) {
    return null;
  }

  return rayParameter;
}

function isForwardEdgeHit(input: {
  edgeParameter: number;
  rayParameter: number;
}): boolean {
  return (
    input.rayParameter >= 0 &&
    input.edgeParameter >= -EDGE_PARAM_EPSILON &&
    input.edgeParameter <= 1 + EDGE_PARAM_EPSILON
  );
}

function isNumber(value: number | null): value is number {
  return value != null;
}

function getPolygonRayHitParameters(input: {
  origin: Point;
  polygon: readonly Point[];
  ray: RayVector;
}): number[] {
  return input.polygon
    .map((_point, edgeIndex) => polygonEdgeAt({ polygon: input.polygon, edgeIndex }))
    .filter((edge): edge is PolygonEdge => edge != null)
    .map((edge) => getRayEdgeParameter({ edge, origin: input.origin, ray: input.ray }))
    .filter(isNumber);
}

/**
 * Intersect the ray from `origin` toward `towards` with a convex polygon's
 * edges, returning the farthest hit along that ray (the silhouette point on the
 * side facing `towards`). `origin` is the node center, which sits inside every
 * convex silhouette, so exactly one forward edge is crossed; we keep the
 * farthest forward hit to be robust to a center that grazes an edge.
 */
function rayPolygonBorderPoint(input: {
  origin: Point;
  towards: Point;
  polygon: readonly Point[];
}): Point | null {
  const ray = resolveRayVector({ origin: input.origin, towards: input.towards });
  if (!ray) {
    return null;
  }

  const hitParameters = getPolygonRayHitParameters({
    origin: input.origin,
    polygon: input.polygon,
    ray,
  });

  if (hitParameters.length < 1) {
    return null;
  }

  const bestParameter = Math.max(...hitParameters);

  return insetTowardCenter({
    point: {
      x: input.origin.x + ray.x * bestParameter,
      y: input.origin.y + ray.y * bestParameter,
    },
    center: input.origin,
  });
}

/**
 * The point where the ray from the circle's center toward `towards` meets the
 * inscribed circle (radius = min(w,h)/2). Used for the connector node so the
 * arrow touches the round edge, not the square bbox corner.
 */
function rayCircleBorderPoint(input: { rect: NodeRect; towards: Point }): Point | null {
  const dx = input.towards.x - input.rect.center.x;
  const dy = input.towards.y - input.rect.center.y;
  const length = Math.hypot(dx, dy);
  if (length < MIN_CENTER_DISTANCE) {
    return null;
  }
  const radius = Math.min(input.rect.size.width, input.rect.size.height) / 2;
  const reach = Math.max(radius - BORDER_INSET, 0);
  return {
    x: input.rect.center.x + (dx / length) * reach,
    y: input.rect.center.y + (dy / length) * reach,
  };
}

/**
 * The point on a node's actual silhouette that lies on the ray from its center
 * toward `towardPoint`. Diamonds, parallelograms, trapezoids and hexagons clip
 * against their inscribed polygon; the connector clips against its inscribed
 * circle; every rounded-rect shape (and any unexpected gap) falls back to the
 * fast bounding-box border clip.
 */
export function getShapeBorderPoint(input: ShapeBorderPointInput): Point {
  const { rect, towardPoint } = input;

  if (rect.shape === FlowchartNodeShape.Connector) {
    const circlePoint = rayCircleBorderPoint({ rect, towards: towardPoint });
    if (circlePoint) {
      return circlePoint;
    }
  }

  const polygon = silhouettePolygon(rect);
  if (polygon) {
    const polygonPoint = rayPolygonBorderPoint({
      origin: rect.center,
      towards: towardPoint,
      polygon,
    });
    if (polygonPoint) {
      return polygonPoint;
    }
  }

  return getNodeBorderPoint({ center: rect.center, size: rect.size, towards: towardPoint });
}

/**
 * The point on a node's silhouette where a ray leaving its center in the
 * `side` cardinal direction (Top/Bottom/Left/Right) exits the shape. This is the
 * exit point for a source (facing its `sourcePosition`) and the entry point for
 * a target (facing its `targetPosition`): straight DOWN out of a top-down
 * source's bottom edge, straight UP into its target's top edge, etc. For a
 * diamond it is the relevant vertex; for a rectangle the edge midpoint.
 */
export function getCardinalBorderPoint(input: { rect: NodeRect; side: Position }): Point {
  const unit = positionUnitVector(input.side);
  const towardPoint: Point = {
    x: input.rect.center.x + unit.x * CARDINAL_PROBE_REACH,
    y: input.rect.center.y + unit.y * CARDINAL_PROBE_REACH,
  };
  return getShapeBorderPoint({ rect: input.rect, towardPoint });
}

export interface OrthogonalEdgeInput {
  source: NodeRect;
  target: NodeRect;
  /** Cardinal side the edge leaves the source from (the source's handle facing). */
  sourcePosition: Position;
  /** Cardinal side the edge enters the target on (the target's handle facing). */
  targetPosition: Position;
  /** Rounded-corner radius for the right-angle bends. */
  borderRadius: number;
}

export interface OrthogonalEdgeGeometry {
  path: string;
  /** Center of the path, sitting on the route — where the label chip is placed. */
  labelPosition: Point;
}

/**
 * A right-angle (stepped) path with rounded corners between the source's
 * cardinal exit point and the target's cardinal entry point, built by
 * react-flow's `getSmoothStepPath`. The two Position values tell it which way
 * each end leaves/arrives so the segments stay axis-aligned. `labelX`/`labelY`
 * from the same util gives a point that sits on the route, used for the label.
 */
export function buildOrthogonalEdge(input: OrthogonalEdgeInput): OrthogonalEdgeGeometry {
  const source = getCardinalBorderPoint({ rect: input.source, side: input.sourcePosition });
  const target = getCardinalBorderPoint({ rect: input.target, side: input.targetPosition });
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX: source.x,
    sourceY: source.y,
    sourcePosition: input.sourcePosition,
    targetX: target.x,
    targetY: target.y,
    targetPosition: input.targetPosition,
    borderRadius: input.borderRadius,
  });
  return { path, labelPosition: { x: labelX, y: labelY } };
}

/** The cardinal side opposite a given {@link Position}. */
function oppositeSide(position: Position): Position {
  if (position === Position.Top) {
    return Position.Bottom;
  }
  if (position === Position.Bottom) {
    return Position.Top;
  }
  if (position === Position.Left) {
    return Position.Right;
  }
  return Position.Left;
}

/**
 * Whether an edge runs UPSTREAM against the flow — its target sits at or before
 * its source along the forward axis (the source's `sourcePosition` direction).
 * A back-edge / loop "No -> next round" arrow flows backward and should be
 * routed out to the side rather than straight up through the forward column.
 *
 * The check projects the source→target center delta onto the forward unit
 * vector; a projection at or below a small threshold means the target is level
 * with or behind the source. Parallel/self edges are handled before this.
 */
const BACK_EDGE_FORWARD_THRESHOLD = 1;

export function isBackEdge(input: {
  source: NodeRect;
  target: NodeRect;
  sourcePosition: Position;
}): boolean {
  const forward = positionUnitVector(input.sourcePosition);
  const deltaX = input.target.center.x - input.source.center.x;
  const deltaY = input.target.center.y - input.source.center.y;
  const forwardProjection = deltaX * forward.x + deltaY * forward.y;
  return forwardProjection <= BACK_EDGE_FORWARD_THRESHOLD;
}

export interface SideRoutedEdgeInput {
  source: NodeRect;
  target: NodeRect;
  /** Forward flow axis (the source's `sourcePosition`), used to pick the margin side. */
  sourcePosition: Position;
  /** Horizontal center of the whole layout; the loop bulges away from it into the margin. */
  layoutCenterX: number;
  /** Vertical center of the whole layout; used for horizontal flows. */
  layoutCenterY: number;
  /** Rounded-corner radius for the right-angle bends. */
  borderRadius: number;
  /** How far into the margin the detour bulges beyond the wider node's edge. */
  marginOffset: number;
}

/**
 * Route a back-edge / loop out to the SIDE and back, so it travels in the open
 * margin instead of cutting straight up through the forward column. Both ends
 * leave/enter on the same lateral side (Left or Right), chosen as the layout's
 * open flank, and `getSmoothStepPath` draws the clean three-segment detour
 * (out, along the margin, back in) with rounded corners. The `offset` pushes the
 * vertical run clear of the node bodies into the margin lane.
 *
 * For a top-down flow the lateral side is Left/Right; for a left-right flow it
 * is Top/Bottom — derived from the forward axis so a back-edge always exits
 * perpendicular to the flow.
 */
export function buildSideRoutedEdge(input: SideRoutedEdgeInput): OrthogonalEdgeGeometry {
  const forward = positionUnitVector(input.sourcePosition);
  const isVerticalFlow = Math.abs(forward.y) > Math.abs(forward.x);

  const side = isVerticalFlow
    ? pickHorizontalMarginSide({ source: input.source, target: input.target, layoutCenterX: input.layoutCenterX })
    : pickVerticalMarginSide({ source: input.source, target: input.target, layoutCenterY: input.layoutCenterY });

  const sourceBorder = getCardinalBorderPoint({ rect: input.source, side });
  const targetBorder = getCardinalBorderPoint({ rect: input.target, side });
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX: sourceBorder.x,
    sourceY: sourceBorder.y,
    sourcePosition: side,
    targetX: targetBorder.x,
    targetY: targetBorder.y,
    targetPosition: side,
    borderRadius: input.borderRadius,
    offset: input.marginOffset,
  });
  return { path, labelPosition: { x: labelX, y: labelY } };
}

/** Pick Left or Right for a side-routed loop in a vertical flow: the open flank. */
function pickHorizontalMarginSide(input: {
  source: NodeRect;
  target: NodeRect;
  layoutCenterX: number;
}): Position {
  const edgeMidX = (input.source.center.x + input.target.center.x) / 2;
  return edgeMidX >= input.layoutCenterX ? Position.Right : Position.Left;
}

/** Pick Top or Bottom for a side-routed loop in a horizontal flow: the open flank. */
function pickVerticalMarginSide(input: {
  source: NodeRect;
  target: NodeRect;
  layoutCenterY: number;
}): Position {
  const edgeMidY = (input.source.center.y + input.target.center.y) / 2;
  return edgeMidY >= input.layoutCenterY ? Position.Bottom : Position.Top;
}

/**
 * For a bowed parallel edge we want the arrowheads to align with the curve's
 * tangent at each end, so we clip each node's silhouette against the control
 * point rather than the far node's center. (A signed perpendicular `offset`
 * fans coincident parallel edges apart so they don't overlap.)
 */
export function getParallelEndpoints(input: {
  source: NodeRect;
  target: NodeRect;
  offset: number;
}): EdgeEndpoints {
  if (input.offset === 0) {
    return {
      source: getShapeBorderPoint({ rect: input.source, towardPoint: input.target.center }),
      target: getShapeBorderPoint({ rect: input.target, towardPoint: input.source.center }),
    };
  }

  const midX = (input.source.center.x + input.target.center.x) / 2;
  const midY = (input.source.center.y + input.target.center.y) / 2;
  const dx = input.target.center.x - input.source.center.x;
  const dy = input.target.center.y - input.source.center.y;
  const length = Math.hypot(dx, dy) || 1;
  const control: Point = {
    x: midX + (-dy / length) * input.offset * 2,
    y: midY + (dx / length) * input.offset * 2,
  };

  return {
    source: getShapeBorderPoint({ rect: input.source, towardPoint: control }),
    target: getShapeBorderPoint({ rect: input.target, towardPoint: control }),
  };
}

/**
 * A quadratic bow between two border points, displaced perpendicular to the
 * segment by `offset`. Used to fan parallel edges apart so they don't overlap.
 * The control point is pushed by `2 * offset` because a quadratic curve only
 * reaches half the control displacement at its apex.
 */
export function buildParallelPath(input: ParallelPathInput): ParallelGeometry {
  const { source, target, offset } = input;
  const midX = (source.x + target.x) / 2;
  const midY = (source.y + target.y) / 2;
  if (offset === 0) {
    return {
      path: `M ${source.x} ${source.y} L ${target.x} ${target.y}`,
      labelPosition: { x: midX, y: midY },
    };
  }

  const dx = target.x - source.x;
  const dy = target.y - source.y;
  const length = Math.hypot(dx, dy) || 1;
  const normalX = -dy / length;
  const normalY = dx / length;
  const controlX = midX + normalX * offset * 2;
  const controlY = midY + normalY * offset * 2;

  return {
    path: `M ${source.x} ${source.y} Q ${controlX} ${controlY} ${target.x} ${target.y}`,
    labelPosition: { x: midX + normalX * offset, y: midY + normalY * offset },
  };
}

/**
 * A self-loop drawn as a clean cubic arc that leaves the node's right border,
 * bows out to the right, and returns to the node's border lower down. Both
 * endpoints sit ON the border so the arrowhead lands cleanly and the arc never
 * disappears behind the node.
 */
export function buildSelfLoopPath(input: SelfLoopInput): SelfLoopGeometry {
  const { center, size, radius } = input;
  const halfWidth = size.width / 2;
  const verticalSpread = Math.min(size.height / 2, radius * 0.5);
  const startX = center.x + halfWidth - BORDER_INSET;
  const startY = center.y - verticalSpread;
  const endX = center.x + halfWidth - BORDER_INSET;
  const endY = center.y + verticalSpread;
  const bulgeX = center.x + halfWidth + radius;

  return {
    path: `M ${startX} ${startY} C ${bulgeX} ${startY - radius * 0.2}, ${bulgeX} ${endY + radius * 0.2}, ${endX} ${endY}`,
    labelPosition: { x: bulgeX + 4, y: center.y },
  };
}
